import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, Pencil, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";

export default function TeamPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "member",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onSubmit = async () => {
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          role: form.role,
          ...(form.password ? { password: form.password } : {}),
        };
        await api.patch(`/users/${editingId}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/users", form);
        toast.success("Member added");
      }
      setOpen(false);
      setEditingId(null);
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onEdit = (u) => {
    setEditingId(u.id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell
      title="Team Members"
      subtitle={`${users.length} of 16 seats used (1 admin + 15 members)`}
      actions={
        <Button
          data-testid="add-member-btn"
          onClick={() => {
            setEditingId(null);
            setForm({ name: "", email: "", password: "", role: "member" });
            setOpen(true);
          }}
          className="bg-brand-700 hover:bg-brand-800 text-white"
        >
          <UserPlus size={16} className="mr-2" /> Add member
        </Button>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
        {users.map((u) => (
          <Card key={u.id} className="tj-card tj-card-hover">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-brand-100 text-brand-800 grid place-items-center font-heading font-semibold">
                  {(u.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-ink-900 truncate">
                      {u.name}
                    </h3>
                    {u.role === "admin" ? (
                      <Badge className="bg-secondary text-white border-0 inline-flex items-center gap-1">
                        <ShieldCheck size={12} /> Admin
                      </Badge>
                    ) : (
                      <Badge className="bg-brand-50 text-brand-800 border-0 inline-flex items-center gap-1">
                        <User size={12} /> Member
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-ink-600 truncate">{u.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`tj-pill ${
                        u.active
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {u.active ? "Active" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid={`edit-member-${u.id}`}
                  onClick={() => onEdit(u)}
                >
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => toggleActive(u)}
                >
                  {u.active ? "Disable" : "Enable"}
                </Button>
                {u.role !== "admin" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(u.id)}
                    data-testid={`delete-member-${u.id}`}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit member" : "Add a team member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                data-testid="member-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="member-email-input"
                type="email"
                value={form.email}
                disabled={!!editingId}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>{editingId ? "New Password (optional)" : "Password"}</Label>
              <Input
                data-testid="member-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-member-btn"
              onClick={onSubmit}
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              {editingId ? "Save changes" : "Create member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
